import React from "react";
import { Box, Button, Checkbox, Stack, Typography } from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";

export default function ActionRow({ action, onClick, onDone, onSmallVersion, onReset, saving = false }) {
  const isComplete = Boolean(action.completedToday || action.completedInWindow);
  const showQuickActions = !isComplete && !action.disabled;
  const hasKeepAliveStep = Boolean(action.backupTitle);
  const supportText = hasKeepAliveStep ? action.backupTitle : action.supportText;
  const supportLabel = hasKeepAliveStep ? "Keep-alive step available" : action.statusLabel || "Support available";
  const handleQuickAction = (event, handler) => {
    event.stopPropagation();
    handler?.();
  };

  return (
    <Box
      onClick={!action.disabled && !saving ? onClick : undefined}
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "28px minmax(0, 1fr)", sm: "30px minmax(0, 1fr) auto" },
        alignItems: "start",
        columnGap: 0.85,
        rowGap: 0.65,
        px: { xs: 1.1, sm: 1.2 },
        py: { xs: 1, sm: 1.05 },
        borderRadius: "16px",
        border: "1px solid",
        borderColor: action.recommended && !isComplete ? "#a9d9bd" : "#e2eaf3",
        backgroundColor: action.recommended && !isComplete ? "#f4fbf7" : "#ffffff",
        cursor: action.disabled || saving ? "default" : "pointer",
        opacity: action.disabled || saving ? 0.72 : 1,
        transition: "transform 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
        "&:hover": action.disabled || saving
          ? undefined
          : {
              transform: "translateY(-1px)",
              borderColor: "#cfdbe8",
              boxShadow: "0 10px 18px rgba(15, 35, 59, 0.055)",
            },
      }}
    >
      <Checkbox
        checked={isComplete}
        disableRipple
        sx={{
          p: 0,
          mt: 0.1,
          color: "#b7c5d5",
          "&.Mui-checked": { color: "#22a267" },
          "& .MuiSvgIcon-root": { fontSize: 23 },
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {(action.statusLabel || (action.recommended && !isComplete)) ? (
          <Typography
            variant="caption"
            sx={{
              display: "inline-flex",
              borderRadius: 999,
              backgroundColor: "#edf7f1",
              color: "#24724f",
              px: 0.7,
              py: 0.16,
              mb: 0.25,
              fontWeight: 900,
              textTransform: "uppercase",
              fontSize: "0.62rem",
            }}
          >
            {action.statusLabel || "Start here"}
          </Typography>
        ) : null}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 760,
            color: "text.primary",
            lineHeight: 1.18,
            fontSize: "1rem",
            textDecoration: isComplete ? "line-through" : "none",
            overflowWrap: "anywhere",
          }}
        >
          {action.title}
        </Typography>
        {supportText && !isComplete ? (
          <Box
            sx={{
              mt: 0.45,
              borderRadius: "13px",
              border: "1px solid #dcefe4",
              backgroundColor: "#f8fffb",
              px: 0.85,
              py: 0.58,
            }}
          >
            <Typography
              variant="caption"
              sx={{ display: "block", color: "#24724f", fontWeight: 900, textTransform: "uppercase", fontSize: "0.62rem" }}
            >
              {supportLabel}
            </Typography>
            <Typography variant="body2" sx={{ color: "#174b34", fontWeight: 760, mt: 0.15, fontSize: "0.86rem" }}>
              {supportText}
            </Typography>
          </Box>
        ) : null}
        {isComplete && action.supportText ? (
          <Typography variant="body2" sx={{ color: "#47715f", mt: 0.28, fontWeight: 750, fontSize: "0.84rem", lineHeight: 1.35 }}>
            {action.supportText}
          </Typography>
        ) : null}
        {action.subtitle ? (
          <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 0.35, color: "#6b7280", mt: 0.18, fontSize: "0.84rem", minWidth: 0 }}>
            <TrackChangesRoundedIcon sx={{ fontSize: 14 }} />
            <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>{action.subtitle}</Box>
          </Typography>
        ) : null}
        {action.doneCriteria ? (
          <Typography variant="body2" sx={{ color: "#334155", mt: 0.28, fontSize: "0.84rem", lineHeight: 1.35 }}>
            <Box component="span" sx={{ fontWeight: 900 }}>Done means:</Box> {action.doneCriteria}
          </Typography>
        ) : null}
        {action.startLabel || action.cueLabel ? (
          <Typography variant="body2" sx={{ color: "#475569", mt: 0.22, fontSize: "0.82rem", lineHeight: 1.35 }}>
            <Box component="span" sx={{ fontWeight: 900 }}>Starts:</Box> {action.startLabel || action.cueLabel}
          </Typography>
        ) : null}
        <Typography variant="body2" sx={{ display: "block", color: "#54667d", mt: 0.25, fontWeight: 700, fontSize: "0.82rem" }}>
          {action.signal}
        </Typography>
        {action.challengeNote ? (
          <Typography
            variant="caption"
            sx={{
              display: "inline-flex",
              mt: 0.45,
              borderRadius: 999,
              backgroundColor: "#eef5f4",
              color: "#2e6256",
              px: 0.75,
              py: 0.22,
              fontWeight: 900,
              fontSize: "0.72rem",
            }}
          >
            {action.challengeNote}
          </Typography>
        ) : null}
        {showQuickActions ? (
          <>
            <Stack
              direction="row"
              spacing={0.65}
              useFlexGap
              sx={{ mt: 0.72, flexWrap: "wrap", rowGap: 0.6 }}
            >
              <Button
                size="small"
                variant="contained"
                disabled={saving}
                onClick={(event) => handleQuickAction(event, onDone || onClick)}
                sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 850, minWidth: 72, py: 0.5 }}
              >
                Done
              </Button>
              {action.backupTitle && onSmallVersion ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={saving}
                  onClick={(event) => handleQuickAction(event, onSmallVersion)}
                  sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 850, py: 0.5 }}
                >
                  Keep it alive
                </Button>
              ) : null}
              {action.canResetToday ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={saving}
                  onClick={(event) => handleQuickAction(event, onReset)}
                  sx={{
                    borderRadius: "999px",
                    textTransform: "none",
                    fontWeight: 850,
                    py: 0.5,
                    borderColor: "#d9b46e",
                    color: "#895900",
                    bgcolor: "#fff8e7",
                    "&:hover": { borderColor: "#c99b44", bgcolor: "#fff4d8" },
                  }}
                >
                  Still here
                </Button>
              ) : null}
            </Stack>
            {action.canResetToday ? (
              <Typography variant="caption" sx={{ display: "block", mt: 0.55, color: "#7a6330", fontWeight: 750 }}>
                Reset records an honest restart. It does not count as done.
              </Typography>
            ) : null}
          </>
        ) : null}
      </Box>
      {saving ? (
        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 900, pt: 1, gridColumn: { xs: "2 / 3", sm: "auto" }, justifySelf: { xs: "start", sm: "end" } }}>
          Saving...
        </Typography>
      ) : showQuickActions ? null : (
        <ChevronRightRoundedIcon sx={{ display: { xs: "none", sm: "block" }, color: "#97a5b8", fontSize: 24, mt: 0.8 }} />
      )}
    </Box>
  );
}
